import { useState, useMemo } from 'react';
import { useApp } from '@/context/AppContext';
import { Sale } from '@/types';
import { calcSale, formatCurrency, generateId, exportToCSV } from '@/utils/calculations';
import StatusBadge from '@/components/base/StatusBadge';
import Modal from '@/components/base/Modal';
import ConfirmDialog from '@/components/base/ConfirmDialog';

const emptyForm = (): Partial<Sale> => ({
  date: new Date().toISOString().split('T')[0],
  customerName: '',
  itemId: '',
  itemName: '',
  quantitySold: 1,
  sellingPrice: 0,
  discount: 0,
  paymentStatus: 'paid',
  notes: '',
});

export default function SalesPage() {
  const { isDark, sales, addSale, updateSale, deleteSale, items } = useApp();
  const allSales = sales;
  const allItems = items;
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState<Sale | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Sale>>(emptyForm());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filtered = useMemo(() => allSales.filter(s => {
    const matchSearch = s.customerName.toLowerCase().includes(search.toLowerCase()) || s.itemName.toLowerCase().includes(search.toLowerCase());
    const matchStatus = !statusFilter || s.paymentStatus === statusFilter;
    return matchSearch && matchStatus;
  }), [allSales, search, statusFilter]);

  const totals = useMemo(() => ({
    revenue: filtered.reduce((s, x) => s + x.totalSaleValue, 0),
    cogs: filtered.reduce((s, x) => s + x.cogs, 0),
    gross: filtered.reduce((s, x) => s + x.grossProfit, 0),
    net: filtered.reduce((s, x) => s + x.netProfit, 0),
  }), [filtered]);

  // Available stock for the currently selected item in the form
  const selectedItem = useMemo(() => allItems.find(i => i.id === form.itemId), [allItems, form.itemId]);
  // When editing, the "available" stock should include the qty already sold in this sale
  const availableStock = useMemo(() => {
    if (!selectedItem) return 0;
    if (editItem && editItem.itemId === selectedItem.id) {
      return selectedItem.currentStock + editItem.quantitySold;
    }
    return selectedItem.currentStock;
  }, [selectedItem, editItem]);

  const isOverselling = useMemo(() => {
    if (!form.itemId || !form.quantitySold) return false;
    return Number(form.quantitySold) > availableStock;
  }, [form.itemId, form.quantitySold, availableStock]);

  const openAdd = () => { setForm(emptyForm()); setEditItem(null); setShowModal(true); };
  const openEdit = (s: Sale) => { setForm({ ...s }); setEditItem(s); setShowModal(true); };

  const handleFormChange = (field: keyof Sale, value: unknown) => {
    setForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'itemId') {
        const item = allItems.find(i => i.id === value);
        if (item) { updated.itemName = item.name; updated.sellingPrice = item.purchasePrice * 1.5; }
      }
      const qty = Number(field === 'quantitySold' ? value : updated.quantitySold ?? 1);
      const price = Number(field === 'sellingPrice' ? value : updated.sellingPrice ?? 0);
      const disc = Number(field === 'discount' ? value : updated.discount ?? 0);
      const itemId = field === 'itemId' ? (value as string) : (updated.itemId ?? '');
      const item = allItems.find(i => i.id === itemId);
      const purchasePrice = item?.purchasePrice ?? 0;
      const calc = calcSale(price, qty, disc, purchasePrice);
      return { ...updated, ...calc };
    });
  };

  const handleSave = () => {
    if (!form.customerName || !form.itemId) return;
    const item = allItems.find(i => i.id === form.itemId);
    const calc = calcSale(
      Number(form.sellingPrice ?? 0),
      Number(form.quantitySold ?? 1),
      Number(form.discount ?? 0),
      item?.purchasePrice ?? 0,
    );
    const sale: Sale = {
      id: editItem?.id ?? generateId(),
      date: form.date ?? '',
      customerName: form.customerName ?? '',
      itemId: form.itemId ?? '',
      itemName: form.itemName ?? '',
      quantitySold: Number(form.quantitySold ?? 1),
      sellingPrice: Number(form.sellingPrice ?? 0),
      discount: Number(form.discount ?? 0),
      paymentStatus: form.paymentStatus ?? 'paid',
      notes: form.notes ?? '',
      ...calc,
    };
    if (editItem) {
      updateSale(sale, editItem);
    } else {
      addSale(sale);
    }
    setShowModal(false);
  };

  const td = `px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`;
  const th = `text-xs font-semibold uppercase tracking-wide px-4 py-3 text-left whitespace-nowrap ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-gray-50 border-gray-200 text-gray-700'}`;

  return (
    <div className="p-4 md:p-6 space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div>
          <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Sales</h2>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{filtered.length} records</p>
        </div>
        <div className="sm:ml-auto flex gap-2">
          <button
            onClick={() => exportToCSV(filtered.map(s => ({ Date: s.date, Customer: s.customerName, Item: s.itemName, Qty: s.quantitySold, Price: s.sellingPrice, Total: s.totalSaleValue, COGS: s.cogs, GrossProfit: s.grossProfit, NetProfit: s.netProfit, Status: s.paymentStatus })), 'sales')}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer whitespace-nowrap border ${isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
          >
            <i className="ri-download-line" /> Export
          </button>
          <button onClick={openAdd} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap">
            <i className="ri-add-line" /> New Sale
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: 'Revenue', value: formatCurrency(totals.revenue), color: 'text-emerald-500' },
          { label: 'COGS', value: formatCurrency(totals.cogs), color: 'text-orange-500' },
          { label: 'Gross Profit', value: formatCurrency(totals.gross), color: 'text-teal-500' },
          { label: 'Net Profit', value: formatCurrency(totals.net), color: 'text-green-600' },
        ].map(c => (
          <div key={c.label} className={`rounded-xl border p-4 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{c.label}</p>
            <p className={`text-lg font-bold mt-1 ${c.color}`}>{c.value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <input
          placeholder="Search customer / item..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={`px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200 placeholder:text-gray-500' : 'bg-white border-gray-200 text-gray-700 placeholder:text-gray-400'}`}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className={`px-3 py-2 rounded-lg border text-sm outline-none cursor-pointer ${isDark ? 'bg-gray-800 border-gray-700 text-gray-200' : 'bg-white border-gray-200 text-gray-700'}`}
        >
          <option value="">All Statuses</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="partial">Partial</option>
        </select>
      </div>

      {/* Table */}
      <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
              <tr>
                <th className={th}>Date</th>
                <th className={th}>Customer</th>
                <th className={th}>Item</th>
                <th className={th}>Qty Sold</th>
                <th className={th}>Avail. Stock</th>
                <th className={th}>Price</th>
                <th className={th}>Discount</th>
                <th className={th}>Total</th>
                <th className={th}>COGS</th>
                <th className={th}>Net Profit</th>
                <th className={th}>Status</th>
                <th className={th}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {filtered.map(s => {
                const itemData = allItems.find(i => i.id === s.itemId);
                const stock = itemData?.currentStock ?? 0;
                return (
                  <tr key={s.id} className={`transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'}`}>
                    <td className={td}>{s.date}</td>
                    <td className={`${td} font-medium`}>{s.customerName}</td>
                    <td className={td}>{s.itemName}</td>
                    <td className={td}>{s.quantitySold}</td>
                    <td className={td}>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        stock <= 0
                          ? 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                          : stock <= (itemData?.lowStockAlert ?? 10)
                          ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400'
                          : 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400'
                      }`}>
                        {stock}
                      </span>
                    </td>
                    <td className={td}>{formatCurrency(s.sellingPrice)}</td>
                    <td className={td}>{s.discount > 0 ? formatCurrency(s.discount) : '—'}</td>
                    <td className={`${td} font-semibold`}>{formatCurrency(s.totalSaleValue)}</td>
                    <td className={td}>{formatCurrency(s.cogs)}</td>
                    <td className={`${td} font-semibold text-emerald-500`}>{formatCurrency(s.netProfit)}</td>
                    <td className={td}><StatusBadge status={s.paymentStatus} /></td>
                    <td className={td}>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(s)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
                          <i className="ri-edit-line text-sm" />
                        </button>
                        <button onClick={() => setDeleteId(s.id)} className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={12} className={`text-center py-10 text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No sales found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'Edit Sale' : 'New Sale'} size="lg">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Date *</label>
            <input type="date" value={form.date ?? ''} onChange={e => handleFormChange('date', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Customer Name *</label>
            <input value={form.customerName ?? ''} onChange={e => handleFormChange('customerName', e.target.value)} className={inputCls} />
          </div>

          {/* Item selector */}
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Item *</label>
            <select
              value={form.itemId ?? ''}
              onChange={e => handleFormChange('itemId', e.target.value)}
              className={`${inputCls} cursor-pointer`}
            >
              <option value="">Select item...</option>
              {allItems.map(i => (
                <option key={i.id} value={i.id}>
                  {i.name} — Stock: {i.currentStock}{i.currentStock <= i.lowStockAlert ? ' ⚠' : ''}
                </option>
              ))}
            </select>
            {selectedItem && (
              <p className={`text-xs mt-1 ${availableStock <= 0 ? 'text-red-500' : availableStock <= selectedItem.lowStockAlert ? 'text-amber-500' : isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Available stock: <strong>{availableStock}</strong> unit{availableStock !== 1 ? 's' : ''}
                {availableStock <= 0 && ' — Out of stock!'}
                {availableStock > 0 && availableStock <= selectedItem.lowStockAlert && ' — Low stock'}
              </p>
            )}
          </div>

          {/* Quantity */}
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Quantity</label>
            <input
              type="number"
              min="1"
              max={availableStock > 0 ? availableStock : undefined}
              value={form.quantitySold ?? 1}
              onChange={e => handleFormChange('quantitySold', e.target.value)}
              className={`${inputCls} ${isOverselling ? 'border-red-400 ring-1 ring-red-400' : ''}`}
            />
            {isOverselling && (
              <p className="text-xs text-red-500 mt-1">
                <i className="ri-error-warning-line mr-1" />
                Quantity exceeds available stock ({availableStock})
              </p>
            )}
          </div>

          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Selling Price</label>
            <input type="number" min="0" value={form.sellingPrice ?? 0} onChange={e => handleFormChange('sellingPrice', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Discount</label>
            <input type="number" min="0" value={form.discount ?? 0} onChange={e => handleFormChange('discount', e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Payment Status</label>
            <select value={form.paymentStatus ?? 'paid'} onChange={e => handleFormChange('paymentStatus', e.target.value)} className={`${inputCls} cursor-pointer`}>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="partial">Partial</option>
            </select>
          </div>

          {/* Calculated summary */}
          <div className={`sm:col-span-2 rounded-lg p-4 grid grid-cols-2 sm:grid-cols-4 gap-3 ${isDark ? 'bg-gray-800' : 'bg-emerald-50'}`}>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Total Sale</p><p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(form.totalSaleValue ?? 0)}</p></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>COGS</p><p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(form.cogs ?? 0)}</p></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Gross Profit</p><p className="text-sm font-bold text-teal-500">{formatCurrency(form.grossProfit ?? 0)}</p></div>
            <div><p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Net Profit</p><p className="text-sm font-bold text-emerald-500">{formatCurrency(form.netProfit ?? 0)}</p></div>
          </div>

          <div className="sm:col-span-2">
            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Notes</label>
            <textarea value={form.notes ?? ''} onChange={e => handleFormChange('notes', e.target.value)} rows={2} className={`${inputCls} resize-none`} />
          </div>
        </div>
        <div className="flex gap-3 justify-end mt-5">
          <button onClick={() => setShowModal(false)} className={`px-4 py-2 rounded-lg text-sm font-medium cursor-pointer whitespace-nowrap ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>Cancel</button>
          <button
            onClick={handleSave}
            disabled={isOverselling}
            className="px-5 py-2 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Sale
          </button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={() => deleteId && deleteSale(deleteId)}
        title="Delete Sale"
        message="Delete this sale record? The sold quantity will be returned to inventory."
      />
    </div>
  );
}
